import React, { useEffect, useState } from "react";
import { Building2, ChevronRight, Loader2, X } from "lucide-react";
import { AppButton, IconButton, PanelButton, StatusBadge } from "../ui";

/**
 * InstitutePicker — modal overlay shown when a user belongs to multiple active
 * institutes. Rendered by Login and Signup after a 300 response.
 *
 * Props:
 *   institutes  — array of { instituteId, instituteName, instituteCode, subdomain, status }
 *   onSelect    — callback(institute) when user picks one
 *   onCancel    — callback when user dismisses
 *   loading     — boolean, disables every option while the pick is re-submitted
 *   title       — optional heading override
 *
 * Each option is a `PanelButton`, the library's "bordered panel that is a
 * button" — the app's hover, focus ring and disabled treatment for free.
 *
 * ⚠️ The options rest on `--card-bg` and the dialog is sectioned with HAIRLINES,
 * not fills. A tinted list region is the obvious alternative and it does not
 * work here: PanelButton's hover is `--bg-hover`, one step off `--bg-tertiary`,
 * so a hovered card would sink INTO a sunken strip rather than lift off it. Nor
 * can a call site repaint that hover — Tailwind emits `hover:tw-bg-surface-hover`
 * AFTER `hover:tw-bg-primary-soft` (checked against the compiled sheet, not
 * assumed), and both are one class, so the primitive wins on source order
 * whatever the className says. Grey is the hover state; white is rest.
 *
 * The backend only ever sends `active` or `trial` (authController), so the map
 * below is total; an unknown value renders no badge rather than an untinted one.
 */
const BADGE_STATUS = { active: "active", trial: "pending" };

/** Up to two initials, for the monogram that anchors each row. */
const initialsOf = (name = "") => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
};

const InstituteOption = ({ institute, busy, disabled, onSelect }) => (
  <PanelButton
    className="tw-group tw-shadow-card hover:tw-shadow-card-hover"
    disabled={disabled}
    onClick={() => onSelect(institute)}
  >
    <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-sm tw-font-semibold tw-text-primary">
      {initialsOf(institute.instituteName)}
    </span>

    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
        <span className="tw-truncate tw-text-[0.95rem] tw-font-semibold tw-text-content">
          {institute.instituteName}
        </span>
        {BADGE_STATUS[institute.status] && (
          <StatusBadge status={BADGE_STATUS[institute.status]}>{institute.status}</StatusBadge>
        )}
      </span>
      {institute.instituteCode && (
        <span className="tw-mt-1 tw-block tw-text-[0.78rem] tw-text-content-subtle">
          Code: {institute.instituteCode}
        </span>
      )}
    </span>

    {busy ? (
      <Loader2 size={18} className="tw-shrink-0 tw-animate-spin tw-text-primary" aria-hidden="true" />
    ) : (
      <ChevronRight
        size={18}
        aria-hidden="true"
        className="tw-shrink-0 tw-text-content-subtle tw-transition-transform group-hover:tw-translate-x-0.5 group-hover:tw-text-primary"
      />
    )}
  </PanelButton>
);

const InstitutePicker = ({ institutes = [], onSelect, onCancel, loading, title }) => {
  // Which row was clicked — so the spinner lands on that option instead of
  // greying all of them identically.
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !loading) onCancel?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel, loading]);

  // A failed pick returns the dialog to rest; without this the row keeps
  // spinning after the parent has already surfaced the error.
  useEffect(() => {
    if (!loading) setPickedId(null);
  }, [loading]);

  if (!institutes.length) return null;

  const handleSelect = (institute) => {
    setPickedId(institute.instituteId);
    onSelect?.(institute);
  };

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-modal tw-flex tw-items-center tw-justify-center tw-bg-overlay tw-p-4 tw-backdrop-blur-[4px] tw-animate-panel-fade"
      onClick={() => !loading && onCancel?.()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="institute-picker-title"
        className="tw-relative tw-flex tw-max-h-[85vh] tw-w-full tw-max-w-[460px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-surface-raised tw-shadow-popover tw-animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="tw-absolute tw-right-3 tw-top-3">
          <IconButton label="Close" icon={X} size="sm" onClick={onCancel} disabled={loading} />
        </span>

        <div className="tw-px-6 tw-pb-4 tw-pt-8 tw-text-center">
          <span className="tw-mx-auto tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded tw-bg-primary-solid tw-text-primary-solid-fg tw-shadow-[0_6px_16px_hsl(var(--primary)/0.35)]">
            <Building2 size={24} aria-hidden="true" />
          </span>
          <h3
            id="institute-picker-title"
            className="tw-mb-1.5 tw-mt-0 tw-text-[1.15rem] tw-font-semibold tw-text-content"
          >
            {title || "Choose Your Institute"}
          </h3>
          <p className="tw-m-0 tw-text-[0.85rem] tw-leading-[1.45] tw-text-content-subtle">
            You are associated with multiple institutes. Please select one to continue.
          </p>
        </div>

        {/* Only this region scrolls, so the heading and Cancel stay reachable on
            a short viewport; its padding also keeps the first option's focus
            outline clear of the scroll container's clipping edge. */}
        <div className="tw-flex tw-flex-col tw-gap-2.5 tw-overflow-y-auto tw-border-0 tw-border-y tw-border-solid tw-border-line tw-px-6 tw-py-4">
          {institutes.map((inst) => (
            <InstituteOption
              key={inst.instituteId}
              institute={inst}
              busy={pickedId === inst.instituteId}
              disabled={loading}
              onSelect={handleSelect}
            />
          ))}
        </div>

        <div className="tw-px-6 tw-py-3.5">
          <AppButton variant="ghost" className="tw-w-full" onClick={onCancel} disabled={loading}>
            Cancel
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default InstitutePicker;
